import { useState, useEffect, useRef } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

type TVTextFieldProps = TextFieldProps & {
  id?: string;
  valor?: (id: string, valor: string) => void;
}

const ALLOWED_KEYS = ['Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

const formatarCentavos = (digitos: string): string => {
  const num = Number(digitos || '0') / 100;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Campo monetário no estilo "calculadora": cada dígito digitado entra pela direita
// (centavos) e o Backspace remove sempre o último dígito. O estado de dígitos é a
// única fonte de verdade — nunca é re-derivado do texto já formatado — o que evita
// o travamento que ocorria ao tentar apagar um valor até zerá-lo.
export const VFieldMoney2FaturamentoPercentual: React.FC<TVTextFieldProps> = ({ valor, id, ...rest }) => {
  const [digitos, setDigitos] = useState('');
  const digitandoRef = useRef(false);
  // Ao focar (clique, tab ou foco programático) o valor atual fica "selecionado":
  // o próximo dígito digitado substitui o valor inteiro em vez de somar à direita.
  const substituirNoProximoDigitoRef = useRef(false);

  useEffect(() => {
    if (digitandoRef.current) { digitandoRef.current = false; return; }
    if (rest.value === undefined || rest.value === null || rest.value === '') { setDigitos(''); return; }
    const centavos = Math.round(Math.abs(Number(rest.value)) * 100);
    setDigitos(centavos > 0 ? String(centavos) : '');
  }, [rest.value]);

  const commit = (novosDigitos: string) => {
    const limpos = novosDigitos.replace(/^0+/, '').slice(0, 12);
    setDigitos(limpos);
    digitandoRef.current = true;
    valor?.(id || '', (Number(limpos || '0') / 100).toFixed(2));
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    substituirNoProximoDigitoRef.current = true;
    e.target.select();
    rest.onFocus?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) { rest.onKeyDown?.(e); return; }
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const substituir = substituirNoProximoDigitoRef.current;
      substituirNoProximoDigitoRef.current = false;
      commit((substituir ? '' : digitos) + e.key);
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      const substituir = substituirNoProximoDigitoRef.current;
      substituirNoProximoDigitoRef.current = false;
      commit(substituir ? '' : digitos.slice(0, -1));
    } else if (!ALLOWED_KEYS.includes(e.key)) {
      e.preventDefault();
    } else {
      substituirNoProximoDigitoRef.current = false;
    }
    rest.onKeyDown?.(e);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const apenasDigitos = e.clipboardData.getData('text').replace(/\D/g, '');
    if (apenasDigitos) commit(digitos + apenasDigitos);
  };

  return (
    <TextField
      {...rest}
      value={formatarCentavos(digitos)}
      onChange={() => {}}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      inputProps={{ inputMode: 'numeric', ...(rest.inputProps || {}) }}
    />
  );
};
